/** The Cart theme-settings group, folded into the `main-cart` blocks that render it.
 *
 *  WHY THIS EXISTS
 *  ---------------
 *  A seller configuring their cart had to visit two panels. "Theme settings → Cart"
 *  held 10 fields; the "Cart drawer" page held the blocks that draw them. The split had
 *  no logic — of the 10, only 3 behaved the way a seller would expect:
 *
 *    - `show_notes` / `show_discount` were read by NOTHING. The `note` / `discount`
 *      blocks render on presence alone, so both checkboxes did nothing while reading as
 *      off, and every store has been showing an order-note box and a discount box
 *      regardless of them. Deleted outright; not bridged here.
 *    - `free_shipping_threshold`, `countdown_minutes`, `countdown_text` were the ONLY
 *      live source, which is what made `progress-bar.threshold` and
 *      `countdown.minutes`/`.text` dead block settings — and `progress-bar`'s help text
 *      promised "leave blank to use the Cart theme setting", which was never true.
 *    - `show_savings` silently GATED the `savings` block: placing it did nothing until a
 *      checkbox on the other panel was ticked.
 *    - `trust_badges`, `upsell_heading`, `upsell_handles` were correct fallbacks.
 *
 *  Everything now lives on the block; the group is gone from `themeSettingsGroups`.
 *
 *  TWO PRECEDENCE FAMILIES, AND WHY
 *  --------------------------------
 *  `blockInstance()` bakes a block def's defaults into stored settings. So every store
 *  whose seller ever opened the builder already carries `countdown.minutes: 10` — the
 *  def default — sitting unread, while its `settings.cart` has no `countdown_minutes`
 *  at all (verified on the demo store). Letting the block win would switch a 10-minute
 *  scarcity countdown ON for every such store on deploy, with nothing to detect it.
 *
 *  So the two families are split by whether the block side was ever LIVE:
 *
 *    A. `countdown_minutes` · `countdown_text` · `free_shipping_threshold` ·
 *       `show_savings` — block side was dead, so its stored value is an unread default
 *       and cannot be trusted. While `theme.settings.cart` EXISTS, legacy is
 *       authoritative and a missing key means off — exactly today's behaviour. The
 *       block wins only once that object is gone.
 *
 *    B. `trust_badges` · `upsell_heading` · `upsell_handles` — block side was already
 *       live and already won when non-blank. Unchanged, because those stored values are
 *       the seller's own.
 *
 *  `normalizeCartBlocks` copies legacy → block and deletes the whole `cart` object, so
 *  the resolvers return the same answer before and after: a provably no-op migration.
 *  The presence of that object is the migrated/un-migrated discriminator — derived, not
 *  a marker. Same two-sided arrangement as `mergeProductUpsellBlocks`: the storefront
 *  resolves at read time so a live store is correct immediately, the builder persists on
 *  load. Both sides import these resolvers rather than re-deriving the precedence, so
 *  the migration cannot disagree with the renderer about what a store shows today.
 */
import { themeSectionLists } from "./section-width.js";
/** Legacy `settings.cart` keys and the block setting each moves onto. Single source of
 *  truth for the migration, the validator's allowlist and the tests.
 *
 *  `show_notes` / `show_discount` are absent deliberately — they were dead, so there is
 *  nothing to move; `normalizeCartBlocks` deletes them separately. */
export const LEGACY_CART_SETTINGS = {
    countdown_minutes: { block: "countdown", to: "minutes" },
    countdown_text: { block: "countdown", to: "text" },
    free_shipping_threshold: { block: "progress-bar", to: "threshold" },
    show_savings: { block: "savings", to: "enabled" },
    trust_badges: { block: "trust-badges", to: "items" },
    upsell_heading: { block: "product-upsell", to: "heading" },
    upsell_handles: { block: "product-upsell", to: "handles" },
    // `upsell_enabled` has no block equivalent: the block being placed with products
    // picked IS the enable (see the storefront's resolveProductUpsell). Listed with a
    // blank target so the migration knows to delete it.
    upsell_enabled: { block: "product-upsell", to: "" },
};
/** Keys that were declared, rendered as controls, and read by nothing. */
export const DEAD_CART_SETTINGS = ["show_notes", "show_discount"];
/** True while this store still carries the pre-merge Cart settings object — i.e. the
 *  builder has not yet migrated it, so family-A legacy values are authoritative. */
export function hasLegacyCartSettings(legacy) {
    return legacy !== undefined && legacy !== null && typeof legacy === "object";
}
// ---- family A: legacy-gated -------------------------------------------------
/** Countdown length in minutes. 0 = off. */
export function cartCountdownMinutes(block, legacy) {
    if (hasLegacyCartSettings(legacy))
        return Number(legacy.countdown_minutes ?? 0) || 0;
    return Number(block?.minutes ?? 0) || 0;
}
export const CART_COUNTDOWN_TEXT_DEFAULT = "Your cart is reserved for";
export function cartCountdownText(block, legacy) {
    const fromLegacy = hasLegacyCartSettings(legacy) ? String(legacy.countdown_text ?? "").trim() : "";
    if (fromLegacy)
        return fromLegacy;
    if (hasLegacyCartSettings(legacy))
        return CART_COUNTDOWN_TEXT_DEFAULT;
    return String(block?.text ?? "").trim() || CART_COUNTDOWN_TEXT_DEFAULT;
}
/** Free-shipping nudge threshold, in MAJOR currency units. 0 = off. */
export function cartFreeShipThreshold(block, legacy) {
    if (hasLegacyCartSettings(legacy))
        return Number(legacy.free_shipping_threshold ?? 0) || 0;
    return Number(block?.threshold ?? 0) || 0;
}
/** Whether the "You saved" total row shows.
 *
 *  The block gets an explicit `enabled` rather than the presence test every other
 *  drawer block uses, because `savings` is in the preset: every store already has the
 *  block, so presence cannot tell the 5 templates that turned it on from the 9 that did
 *  not. Its default is FALSE for the same reason — a store whose `settings.cart` never
 *  existed must keep rendering nothing. */
export function cartShowSavings(block, legacy) {
    if (hasLegacyCartSettings(legacy))
        return legacy.show_savings === true;
    return block?.enabled === true;
}
// ---- family B: block already won when non-blank -----------------------------
/** Trust-badge lines, "emoji|text" per line. */
export function cartTrustBadges(block, legacy) {
    return String(block?.items ?? "").trim() || String(legacy?.trust_badges ?? "");
}
// ---- migration --------------------------------------------------------------
/** The value to persist onto the block for one legacy key, or undefined to skip. */
function migratedValue(key, block, legacy) {
    switch (key) {
        case "countdown_minutes":
            return cartCountdownMinutes(block, legacy);
        case "countdown_text":
            return cartCountdownText(block, legacy);
        case "free_shipping_threshold": {
            const n = cartFreeShipThreshold(block, legacy);
            // Blank rather than "0": the field is a text input whose empty state means off,
            // and a literal 0 in a money box reads as a real threshold of zero.
            return n > 0 ? String(n) : "";
        }
        case "show_savings":
            return cartShowSavings(block, legacy);
        case "trust_badges":
            return cartTrustBadges(block, legacy);
        case "upsell_heading":
            return String(block?.heading ?? "").trim() || String(legacy.upsell_heading ?? "");
        case "upsell_handles":
            return String(block?.handles ?? "").trim() || String(legacy.upsell_handles ?? "");
        default:
            return undefined; // upsell_enabled — delete only
    }
}
function migrateSection(section, legacy) {
    if (section.type !== "main-cart" || !section.blocks?.length)
        return;
    const byType = new Map();
    for (const b of section.blocks)
        if (!byType.has(b.type))
            byType.set(b.type, b);
    for (const [key, { block: blockType, to }] of Object.entries(LEGACY_CART_SETTINGS)) {
        if (!to)
            continue;
        const target = byType.get(blockType);
        // No such block placed: nothing to migrate onto, and with no block there is nothing
        // to render either — so dropping the legacy key loses nothing.
        if (!target)
            continue;
        const value = migratedValue(key, target.settings, legacy);
        if (value === undefined)
            continue;
        target.settings ??= {};
        target.settings[to] = value;
    }
}
/** Move every legacy `settings.cart` value onto the `main-cart` block that renders it,
 *  then delete the whole `cart` object, in place.
 *
 *  A no-op by construction: the resolvers above return the same answer before (object
 *  present, legacy authoritative) and after (object gone, block carries the copied
 *  value). Returns true when the document changed, so a caller can decide whether to
 *  persist. Idempotent — once the object is gone there is nothing left to move. */
export function normalizeCartBlocks(theme) {
    const settings = theme.settings;
    const legacy = settings?.cart;
    if (!hasLegacyCartSettings(legacy))
        return false;
    for (const list of themeSectionLists(theme)) {
        for (const section of list) {
            migrateSection(section, legacy);
            for (const child of section.children ?? [])
                migrateSection(child, legacy);
        }
    }
    // The whole object goes, not just its keys: its PRESENCE is what tells the resolvers
    // a store is un-migrated, so leaving an empty `{}` behind would keep every family-A
    // reader on the legacy branch forever and permanently ignore the block values just
    // written.
    delete settings.cart;
    return true;
}
//# sourceMappingURL=cart-settings.js.map